<template>
  <main class="terminal-consulta">
    <Keypress
      key-event="keyup"
      :prevent-default="true"
      :key-code="teclasEnum.F2"
      @success="buscarProduto"
    />

    <header class="terminal-consulta__busca">
      <CardBuscaTerminal>
        <template #formulario>
          <Select
            ref="buscarProduto"
            v-model="produto"
            input-class="terminal-search"
            placeholder="Informe o código, descrição, código de barras ou referência do produto"
            label-busca="descricao"
            :options="produtos"
            :change="setProduto"
            :search="onSearch"
            focus
          >
            <template #options="props">
              <div v-if="!props.data.isKit" class="resultado-busca">
                <div class="resultado-busca__titulo">
                  <span
                    >{{ props.data.codigo }} - {{ props.data.descricao }}</span
                  >
                  <span class="resultado-busca__marcadores">
                    <small v-if="possuiVariacao(props.data)">Variações</small>
                    <small v-if="possuiLote(props.data)">Lotes</small>
                  </span>
                </div>
                <div class="resultado-busca__resumo">
                  <span>
                    R$ {{ numberToReal(parseFloat(props.data.preco), "preco")
                    }}{{ getPrecoAtacadoInfo(props.data) }}
                  </span>
                  <span>
                    Qtd.:
                    {{
                      numberToReal(
                        parseFloat(props.data.quantidade),
                        "quantidade"
                      )
                    }}
                  </span>
                </div>
              </div>
              <div v-else class="resultado-busca resultado-busca--kit">
                <span>
                  {{ props.data.codigo }} - {{ props.data.descricao }}
                  <span
                    id="kitInfo"
                    :title="getKitInfo(props.data)"
                    v-b-tooltip.hover.html.right
                  >
                    <InformationOutline :size="14" />
                  </span>
                </span>
                <span class="text-muted">
                  R$ {{ numberToReal(props.data.totalPrice, "preco") }}
                </span>
              </div>
            </template>
          </Select>
        </template>
      </CardBuscaTerminal>
    </header>

    <section v-if="!produto" class="terminal-consulta__vazio">
      <div class="terminal-consulta__icone">
        <BarcodeScan :size="96" />
      </div>
      <h1>Consulte um produto</h1>
      <p>Use a busca acima ou pressione F2 para começar.</p>
    </section>

    <section v-else class="produto-consultado" aria-live="polite">
      <article class="produto-resumo">
        <div v-if="urlProdutoImage" class="produto-resumo__imagem">
          <img :src="urlProdutoImage" :alt="`Imagem de ${produto.descricao}`" />
        </div>
        <div
          v-else
          class="produto-resumo__imagem produto-resumo__imagem--vazia"
        >
          <BarcodeScan :size="44" />
        </div>

        <div class="produto-resumo__conteudo">
          <div class="produto-resumo__codigo">Código {{ produto.codigo }}</div>
          <h1>{{ produto.descricao }}</h1>
          <div class="produto-resumo__marcadores">
            <span v-if="temVariacoes" class="marcador marcador--variacao">
              Produto com variações
            </span>
            <span v-if="temLotes" class="marcador marcador--lote">
              Produto com lotes
            </span>
          </div>
        </div>

        <button type="button" class="nova-consulta" @click="buscarProduto">
          Nova consulta <span class="nova-consulta__atalho">F2</span>
        </button>
      </article>

      <div class="produto-indicadores">
        <article class="indicador indicador--preco">
          <span>Preço</span>
          <strong>R$ {{ numberToReal(produto.preco, "preco") }}</strong>
        </article>
        <article
          v-if="getPrecoAtacado(produto) > 0"
          class="indicador indicador--atacado"
        >
          <span>Preço no atacado</span>
          <strong>
            R$ {{ numberToReal(getPrecoAtacado(produto), "preco") }}
          </strong>
        </article>
        <article class="indicador indicador--estoque">
          <span>Estoque total disponível</span>
          <strong>{{ numberToReal(produto.quantidade, "quantidade") }}</strong>
          <small>{{ formatarUnidadeMedida(produto.unidade_medida) }}</small>
        </article>
      </div>

      <div v-if="carregandoDetalhes" class="detalhes-carregando">
        <b-spinner small label="Carregando detalhes" />
        <span>Carregando variações e lotes...</span>
      </div>

      <b-alert v-if="erroDetalhes" show variant="warning" class="mb-4">
        {{ erroDetalhes }}
      </b-alert>

      <section v-if="temVariacoes" class="estoque-detalhe">
        <div class="estoque-detalhe__cabecalho">
          <div>
            <span class="estoque-detalhe__rotulo">Estoque detalhado</span>
            <h2>Variações</h2>
            <p>Saldo disponível em cada combinação do produto.</p>
          </div>
          <label v-if="variacoes.length > 6" class="filtro-detalhes">
            <span class="sr-only">Filtrar variações</span>
            <input
              v-model.trim="filtroVariacao"
              type="search"
              placeholder="Filtrar por cor, tamanho..."
            />
          </label>
        </div>

        <div v-if="variacoesFiltradas.length" class="variacoes-grid">
          <article
            v-for="variacao in variacoesFiltradas"
            :key="`variacao-${variacao.id}`"
            class="variacao-card"
          >
            <div class="variacao-card__atributos">
              <div
                v-for="atributo in variacao.atributos"
                :key="`${variacao.id}-${atributo.nome}`"
                class="atributo"
              >
                <span>{{ atributo.nome }}</span>
                <strong>{{ atributo.valor }}</strong>
              </div>
            </div>
            <div
              class="saldo"
              :class="classeSaldo(variacao.quantidade)"
              :aria-label="`${variacao.quantidade} unidades disponíveis`"
            >
              <strong>
                {{ numberToReal(variacao.quantidade, "quantidade") }}
              </strong>
              <span>disponível</span>
            </div>
          </article>
        </div>
        <div v-else class="estoque-detalhe__vazio">
          {{
            filtroVariacao
              ? "Nenhuma variação corresponde ao filtro."
              : "Nenhuma variação cadastrada para exibir."
          }}
        </div>
      </section>

      <section v-if="temLotes" class="estoque-detalhe">
        <div class="estoque-detalhe__cabecalho">
          <div>
            <span class="estoque-detalhe__rotulo">Rastreabilidade</span>
            <h2>Lotes</h2>
            <p>Validade e saldo disponível por lote.</p>
          </div>
          <label v-if="lotes.length > 6" class="filtro-detalhes">
            <span class="sr-only">Filtrar lotes</span>
            <input
              v-model.trim="filtroLote"
              type="search"
              placeholder="Filtrar pelo número do lote"
            />
          </label>
        </div>

        <div v-if="lotesFiltrados.length" class="lotes-grid">
          <article
            v-for="lote in lotesFiltrados"
            :key="`lote-${lote.id}`"
            class="lote-card"
          >
            <div class="lote-card__topo">
              <div>
                <span>Número do lote</span>
                <strong>{{ lote.numero }}</strong>
              </div>
              <span v-if="loteVencido(lote)" class="lote-vencido">Vencido</span>
            </div>
            <dl class="lote-card__datas">
              <div>
                <dt>Fabricação</dt>
                <dd>{{ formatarDataLote(lote.dataFabricacao) }}</dd>
              </div>
              <div>
                <dt>Validade</dt>
                <dd>{{ formatarDataLote(lote.dataValidade) }}</dd>
              </div>
            </dl>
            <div
              class="saldo saldo--lote"
              :class="classeSaldo(lote.quantidade)"
            >
              <strong>{{ numberToReal(lote.quantidade, "quantidade") }}</strong>
              <span>disponível</span>
            </div>
          </article>
        </div>
        <div v-else class="estoque-detalhe__vazio">
          {{
            filtroLote
              ? "Nenhum lote corresponde ao filtro."
              : "Nenhum lote cadastrado para exibir."
          }}
        </div>
      </section>

      <footer class="produto-consultado__rodape">
        <button
          type="button"
          class="nova-consulta nova-consulta--rodape"
          @click="buscarProduto"
        >
          Consultar outro produto
        </button>
      </footer>
    </section>
  </main>
</template>

<script>
import { mapGetters } from "vuex";
import _ from "lodash";
import BarcodeScan from "vue-material-design-icons/BarcodeScan";
import InformationOutline from "vue-material-design-icons/InformationOutline.vue";
import { SistemaMixin } from "@/modules/Application";
import { CardBuscaTerminal } from "../../Pdv/components/Cards";
import { Select } from "../../Pdv/components/Form";
import { TECLAS } from "../../Pdv/enums/Documento";
import { quantityOptions } from "@/modules/Application";
import {
  filtrarPorTexto,
  formatarUnidadeMedida,
  normalizarLotes,
  normalizarVariacoes,
} from "./terminalConsultaHelpers";

export default {
  name: "app-terminal-consulta",
  mixins: [SistemaMixin],
  components: {
    BarcodeScan,
    CardBuscaTerminal,
    InformationOutline,
    Select,
    Keypress: () => import("vue-keypress"),
  },
  data() {
    return {
      quantityOptions,
      quantidade: 0,
      produto: null,
      produtos: [],
      teclasEnum: TECLAS,
      urlProdutoImage: null,
      carregandoDetalhes: false,
      erroDetalhes: "",
      filtroVariacao: "",
      filtroLote: "",
      consultaAtual: 0,
    };
  },
  computed: {
    ...mapGetters(["layoutConfig", "itsblocked"]),
    config() {
      return this.layoutConfig();
    },
    variacoes() {
      return normalizarVariacoes(this.produto || {});
    },
    lotes() {
      return normalizarLotes(this.produto || {});
    },
    temVariacoes() {
      return this.variacoes.length > 0 || this.possuiVariacao(this.produto);
    },
    temLotes() {
      return this.lotes.length > 0 || this.possuiLote(this.produto);
    },
    variacoesFiltradas() {
      return filtrarPorTexto(this.variacoes, this.filtroVariacao, (item) =>
        item.atributos.flatMap((atributo) => [atributo.nome, atributo.valor])
      );
    },
    lotesFiltrados() {
      return filtrarPorTexto(this.lotes, this.filtroLote, (item) => [
        item.numero,
      ]);
    },
  },
  mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
    }
  },
  methods: {
    buscarProduto() {
      this.consultaAtual += 1;
      this.produto = null;
      this.produtos = [];
      this.urlProdutoImage = null;
      this.erroDetalhes = "";
      this.filtroVariacao = "";
      this.filtroLote = "";
      this.carregandoDetalhes = false;

      this.$nextTick(() => {
        const input = this.$refs.buscarProduto?.$el?.querySelector("input");
        if (input) input.focus();
      });
    },
    onSearch(search, loading) {
      if (search.length > 1 && search[0] === "+") {
        loading(true);
        this.searchKit(loading, search, this);
        return;
      }

      if (search.length > 2 || /^[0-9]+$/.test(search)) {
        this.search(loading, search, this);
      }
    },
    search: _.debounce(async (loading, search, vm) => {
      if (search === "" || search === null) return loading(false);

      let busca = search;
      if (busca.split("*").length > 1) {
        let quantidade = 1 * 10 * vm.quantityOptions.precision;
        [quantidade, busca] = busca.split("*");
        vm.quantidade = vm.numberToReal(parseFloat(quantidade), "quantidade");
      }

      if (busca === "") return loading(false);

      loading(true);
      try {
        const data = await vm.fetchProdutos(busca, vm.typeFilter);
        vm.produtos = data;

        if (
          data.length === 1 &&
          vm.isBarCode(busca) &&
          Number.parseInt(busca)
        ) {
          await vm.setProduto(data[0]);
        }
      } finally {
        loading(false);
      }

      return true;
    }, 350),
    searchKit: _.debounce(async (loading, search, vm) => {
      try {
        const { data } = await vm.$http.post("/kit/get-kit", { search });
        vm.produtos = data.map((kit) => ({
          ...kit,
          descricao: kit.nome,
          isKit: true,
        }));
      } finally {
        loading(false);
      }
    }, 350),
    async setProduto(produto) {
      if (!produto) return false;

      const idConsulta = ++this.consultaAtual;
      this.produto = produto;
      this.urlProdutoImage = produto.imagem;
      this.erroDetalhes = "";
      this.filtroVariacao = "";
      this.filtroLote = "";
      this.carregandoDetalhes = true;

      try {
        const produtoCompleto =
          (await this.getProdutoCompleto(produto)) || produto;
        const produtoComDetalhes = await this.carregarDadosComplementares(
          produtoCompleto
        );

        if (idConsulta !== this.consultaAtual) return false;

        this.produto = { ...produto, ...produtoComDetalhes };
        this.urlProdutoImage = this.produto.imagem;
      } catch (error) {
        console.error(error);
        if (idConsulta === this.consultaAtual) {
          this.erroDetalhes =
            "Não foi possível carregar todos os detalhes de estoque deste produto.";
        }
      } finally {
        if (idConsulta === this.consultaAtual) this.carregandoDetalhes = false;
      }

      return true;
    },
    async getProdutoCompleto(produto) {
      const id = produto?.id || produto?.id_produto || produto?.codigo;
      if (!id) return null;

      const { data } = await this.$http.post("/produtos/find", { id });
      return data;
    },
    async carregarDadosComplementares(produto) {
      const idProduto = produto?.id || produto?.id_produto;
      const precisaVariacoes =
        idProduto &&
        this.possuiVariacao(produto) &&
        normalizarVariacoes(produto).length === 0;
      const precisaLotes =
        idProduto &&
        this.possuiLote(produto) &&
        normalizarLotes(produto).length === 0;
      const requisicoes = [];

      if (precisaVariacoes) {
        requisicoes.push(
          this.$http
            .post("/produtos/get-combinacao", { id_produto: idProduto })
            .then(({ data }) => ({
              combinacao: data?.combinacao || data || [],
            }))
            .catch((error) => {
              console.error(error);
              return { erroComplemento: true };
            })
        );
      }

      if (precisaLotes) {
        requisicoes.push(
          this.$http
            .post("/produtos/get-lote", {
              id_produto: idProduto,
              compra: true,
            })
            .then(({ data }) => ({ lotes: data || [] }))
            .catch((error) => {
              console.error(error);
              return { erroComplemento: true };
            })
        );
      }

      const detalhes = await Promise.all(requisicoes);
      if (detalhes.some((detalhe) => detalhe.erroComplemento)) {
        this.erroDetalhes =
          "Alguns detalhes de estoque não puderam ser carregados.";
      }

      return Object.assign({}, produto, ...detalhes);
    },
    possuiVariacao(produto) {
      return (
        this.valorBooleano(produto?.variavel) ||
        this.valorBooleano(produto?.possui_variacao)
      );
    },
    possuiLote(produto) {
      return (
        this.valorBooleano(produto?.lote) ||
        this.valorBooleano(produto?.possui_lote)
      );
    },
    valorBooleano(valor) {
      return valor === true || valor === 1 || valor === "1" || valor === "true";
    },
    getPrecoAtacado(produto) {
      return this.getValorNumerico(produto?.preco_atacado);
    },
    formatarUnidadeMedida,
    getValorNumerico(valor) {
      if (valor === null || valor === undefined || valor === "") return 0;
      if (typeof valor === "number") return valor;

      return this.formatNumberToFloat(valor);
    },
    getPrecoAtacadoInfo(produto) {
      const precoAtacado = this.getPrecoAtacado(produto);
      return precoAtacado > 0
        ? ` | Atacado: R$ ${this.numberToReal(precoAtacado, "preco")}`
        : "";
    },
    getKitInfo(kitData) {
      if (kitData.usar_unidade_medida) {
        const unidade = kitData.unidade_medida || {};
        const base = kitData.productOfTradeCollection?.[0]?.product || {};
        const conversor = parseFloat(unidade.conversor) || 0;
        return (
          `1 ${unidade.sigla || ""} de ${base.descricao || ""}` +
          (conversor
            ? ` (${conversor} ${base.unidade_medida?.sigla || "un"})`
            : "") +
          ` - R$ ${this.numberToReal(kitData.valor_unitario, "preco")}`
        );
      }

      return kitData.productOfTradeCollection.reduce(
        (descricao, item, index) =>
          `${descricao}${index + 1} - ${item.product.descricao} - QTD: ${
            item.product.quantidade
          }<br/>`,
        ""
      );
    },
    classeSaldo(quantidade) {
      if (Number(quantidade) < 0) return "saldo--negativo";
      if (Number(quantidade) === 0) return "saldo--zerado";
      return "saldo--positivo";
    },
    formatarDataLote(data) {
      if (!data) return "Não informada";
      const partes = String(data).slice(0, 10).split("-");
      if (partes.length !== 3) return String(data);
      return `${partes[2]}/${partes[1]}/${partes[0]}`;
    },
    loteVencido(lote) {
      if (!lote.dataValidade) return false;
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      return (
        new Date(`${String(lote.dataValidade).slice(0, 10)}T00:00:00`) < hoje
      );
    },
    isBarCode(codigo) {
      return [8, 12, 13, 14].includes(codigo.length);
    },
  },
};
</script>

<style scoped>
.terminal-consulta {
  --terminal-bg: #f4f6f8;
  --terminal-text: #20262e;
  --terminal-muted: #687383;
  --terminal-border: #e2e7ec;
  min-height: calc(100vh - 80px);
  padding-bottom: 2rem;
  color: var(--terminal-text);
  background: var(--terminal-bg);
}

.terminal-consulta__busca {
  position: sticky;
  top: 0;
  z-index: 20;
  box-shadow: 0 4px 18px rgba(22, 33, 48, 0.1);
}

.terminal-consulta__busca ::v-deep .container-app,
.terminal-consulta__busca ::v-deep .card {
  margin: 0;
  border-radius: 0;
}

.terminal-consulta__busca ::v-deep .terminal-search .vs__dropdown-toggle {
  min-height: 52px;
  padding: 4px 8px;
  border: 0;
  border-radius: 8px;
  background: #fff;
}

.terminal-consulta__busca ::v-deep .terminal-search .vs__search,
.terminal-consulta__busca ::v-deep .terminal-search .vs__selected {
  color: var(--terminal-text);
  font-size: 1.05rem;
  font-weight: 600;
}

.terminal-consulta__busca ::v-deep .terminal-search .vs__actions {
  display: none;
}

.terminal-consulta__busca ::v-deep .terminal-search .vs__dropdown-menu {
  max-height: min(55vh, 420px);
  border: 0;
  box-shadow: 0 10px 30px rgba(22, 33, 48, 0.16);
}

.resultado-busca {
  padding: 0.35rem 0;
  text-transform: uppercase;
}

.resultado-busca__titulo,
.resultado-busca__resumo,
.resultado-busca--kit {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.resultado-busca__titulo {
  font-weight: 700;
}

.resultado-busca__resumo {
  margin-top: 0.25rem;
  color: #5f6b79;
  font-size: 0.82rem;
}

.resultado-busca__marcadores {
  display: flex;
  gap: 0.3rem;
}

.resultado-busca__marcadores small {
  padding: 0.18rem 0.4rem;
  border-radius: 999px;
  color: #475569;
  background: #e9eef5;
  white-space: nowrap;
}

.terminal-consulta__vazio {
  min-height: calc(100vh - 230px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  text-align: center;
}

.terminal-consulta__icone {
  width: 150px;
  height: 150px;
  display: grid;
  place-items: center;
  margin-bottom: 1.5rem;
  border-radius: 50%;
  color: var(--primary-color);
  background: #fff;
  box-shadow: 0 10px 30px rgba(22, 33, 48, 0.08);
}

.terminal-consulta__vazio h1 {
  margin: 0 0 0.5rem;
  font-size: clamp(1.7rem, 4vw, 2.5rem);
}

.terminal-consulta__vazio p {
  margin: 0;
  color: var(--terminal-muted);
  font-size: 1.05rem;
}

.produto-consultado {
  width: min(1180px, calc(100% - 2rem));
  margin: 0 auto;
  padding-top: 1.5rem;
}

.produto-resumo {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  border: 1px solid var(--terminal-border);
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(22, 33, 48, 0.06);
}

.produto-resumo__imagem {
  width: 120px;
  height: 120px;
  overflow: hidden;
  border-radius: 12px;
  background: #f3f4f6;
}

.produto-resumo__imagem img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.produto-resumo__imagem--vazia {
  display: grid;
  place-items: center;
  color: #9aa4b2;
}

.produto-resumo__codigo,
.estoque-detalhe__rotulo {
  color: var(--primary-color);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.produto-resumo h1 {
  margin: 0.3rem 0 0.65rem;
  color: var(--terminal-text);
  font-size: clamp(1.45rem, 3vw, 2.25rem);
  line-height: 1.15;
  overflow-wrap: anywhere;
}

.produto-resumo__marcadores {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.marcador {
  min-height: 26px;
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.marcador--variacao {
  color: #7c4a03;
  background: #fff2cc;
}

.marcador--lote {
  color: #075985;
  background: #e0f2fe;
}

.nova-consulta {
  min-height: 46px;
  padding: 0.7rem 1rem;
  border: 1px solid var(--primary-color);
  border-radius: 9px;
  color: var(--primary-color);
  background: #fff;
  font-weight: 700;
  cursor: pointer;
}

.nova-consulta:hover,
.nova-consulta:focus {
  color: #fff;
  background: var(--primary-color);
  outline: none;
}

.nova-consulta__atalho {
  margin-left: 0.4rem;
  padding: 0.15rem 0.35rem;
  border-radius: 4px;
  color: inherit;
  background: rgba(127, 127, 127, 0.12);
  font-size: 0.72rem;
}

.produto-indicadores {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.8rem;
  margin: 0.8rem 0 1.5rem;
}

.indicador {
  position: relative;
  min-height: 112px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 1.1rem 1.25rem 1.1rem 1.45rem;
  overflow: hidden;
  border: 1px solid var(--terminal-border);
  border-radius: 13px;
  background: #fff;
}

.indicador::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 5px;
  background: var(--primary-color);
}

.indicador--atacado::before {
  background: #b7791f;
}

.indicador--estoque::before {
  background: #2f855a;
}

.indicador span,
.indicador small {
  color: var(--terminal-muted);
}

.indicador strong {
  margin-top: 0.2rem;
  color: var(--terminal-text);
  font-size: clamp(1.45rem, 3vw, 2rem);
  line-height: 1.15;
}

.detalhes-carregando {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 1rem;
  margin-bottom: 1rem;
  color: var(--terminal-muted);
}

.estoque-detalhe {
  padding: 1.35rem;
  margin-bottom: 1.25rem;
  border: 1px solid var(--terminal-border);
  border-radius: 16px;
  background: #fff;
}

.estoque-detalhe__cabecalho {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.estoque-detalhe h2 {
  margin: 0.15rem 0 0;
  color: var(--terminal-text);
  font-size: 1.45rem;
}

.estoque-detalhe p {
  margin: 0.25rem 0 0;
  color: var(--terminal-muted);
}

.filtro-detalhes {
  width: min(330px, 100%);
  margin: 0;
}

.filtro-detalhes input {
  width: 100%;
  min-height: 44px;
  padding: 0.65rem 0.8rem;
  border: 1px solid #cbd3dc;
  border-radius: 9px;
  color: var(--terminal-text);
  background: #fff;
}

.filtro-detalhes input:focus {
  border-color: var(--primary-color);
  outline: none;
  box-shadow: 0 0 0 3px rgba(70, 90, 220, 0.12);
}

.variacoes-grid,
.lotes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 0.7rem;
}

.variacao-card,
.lote-card {
  min-width: 0;
  border: 1px solid var(--terminal-border);
  border-radius: 11px;
  background: #fbfcfd;
}

.variacao-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
}

.variacao-card__atributos {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(85px, 1fr));
  gap: 0.65rem;
  padding: 0.9rem;
}

.atributo span,
.lote-card__topo span,
.lote-card dt {
  display: block;
  margin-bottom: 0.1rem;
  color: var(--terminal-muted);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.atributo strong,
.lote-card__topo strong {
  display: block;
  color: var(--terminal-text);
  font-size: 1rem;
  overflow-wrap: anywhere;
}

.saldo {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  padding: 0.6rem 0.9rem;
  border-top: 1px solid currentColor;
}

.saldo strong {
  font-size: 1.12rem;
}

.saldo span {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}

.saldo--positivo {
  color: #17643a;
  background: #eaf8f0;
}

.saldo--zerado {
  color: #687383;
  background: #f0f2f4;
}

.saldo--negativo {
  color: #a22525;
  background: #fcecec;
}

.lote-card {
  overflow: hidden;
}

.lote-card__topo {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.7rem;
  padding: 0.9rem;
}

.lote-card__topo strong {
  font-size: 1.08rem;
}

.lote-card__topo .lote-vencido {
  flex: 0 0 auto;
  padding: 0.25rem 0.45rem;
  border-radius: 999px;
  color: #9b1c1c;
  background: #fee2e2;
  font-size: 0.68rem;
}

.lote-card__datas {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.7rem;
  padding: 0 0.9rem 0.9rem;
  margin: 0;
}

.lote-card dd {
  margin: 0;
  color: var(--terminal-text);
  font-weight: 600;
}

.saldo--lote {
  justify-content: flex-end;
}

.estoque-detalhe__vazio {
  padding: 1.5rem 1rem;
  border: 1px dashed #cbd3dc;
  border-radius: 10px;
  color: var(--terminal-muted);
  text-align: center;
}

.produto-consultado__rodape {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0 1rem;
}

.nova-consulta--rodape {
  display: none;
}

@media (max-width: 767px) {
  .terminal-consulta {
    min-height: 100vh;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom));
  }

  .terminal-consulta__busca {
    position: relative;
  }

  .terminal-consulta__busca ::v-deep .card-header {
    padding: 0.35rem 0.45rem;
  }

  .terminal-consulta__busca ::v-deep .card-body {
    padding: 0.35rem;
  }

  .terminal-consulta__busca ::v-deep .terminal-search .vs__dropdown-toggle {
    min-height: 50px;
  }

  .terminal-consulta__busca ::v-deep .terminal-search .vs__search,
  .terminal-consulta__busca ::v-deep .terminal-search .vs__selected {
    font-size: 0.94rem;
  }

  .resultado-busca__titulo,
  .resultado-busca__resumo {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.25rem;
  }

  .resultado-busca__marcadores {
    order: -1;
  }

  .produto-consultado {
    width: calc(100% - 1rem);
    padding-top: 0.5rem;
  }

  .produto-resumo {
    grid-template-columns: 76px minmax(0, 1fr);
    gap: 0.85rem;
    padding: 0.9rem;
    border-radius: 12px;
  }

  .produto-resumo__imagem {
    width: 76px;
    height: 76px;
  }

  .produto-resumo__conteudo:first-child {
    grid-column: 1 / -1;
  }

  .produto-resumo h1 {
    margin-bottom: 0.5rem;
    font-size: 1.25rem;
  }

  .produto-resumo > .nova-consulta {
    display: none;
  }

  .produto-indicadores {
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    margin: 0.5rem 0 1rem;
  }

  .indicador {
    min-height: 94px;
    padding: 0.85rem 0.75rem 0.85rem 1rem;
    border-radius: 10px;
  }

  .indicador:last-child:nth-child(odd) {
    grid-column: 1 / -1;
  }

  .indicador span {
    font-size: 0.76rem;
  }

  .indicador strong {
    font-size: 1.3rem;
  }

  .estoque-detalhe {
    padding: 0.9rem;
    border-radius: 12px;
  }

  .estoque-detalhe__cabecalho {
    align-items: stretch;
    flex-direction: column;
    gap: 0.75rem;
  }

  .estoque-detalhe h2 {
    font-size: 1.25rem;
  }

  .estoque-detalhe p {
    font-size: 0.85rem;
  }

  .filtro-detalhes {
    width: 100%;
  }

  .variacoes-grid,
  .lotes-grid {
    grid-template-columns: 1fr;
    gap: 0.55rem;
  }

  .nova-consulta--rodape {
    display: block;
    width: 100%;
    color: #fff;
    background: var(--primary-color);
  }
}

@media (max-width: 390px) {
  .produto-indicadores {
    grid-template-columns: 1fr;
  }

  .indicador:last-child:nth-child(odd) {
    grid-column: auto;
  }

  .produto-resumo {
    grid-template-columns: 64px minmax(0, 1fr);
  }

  .produto-resumo__imagem {
    width: 64px;
    height: 64px;
  }
}
</style>
